import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Trash2 } from 'lucide-react';
import RunAuditButton from '../../../components/admin/RunAuditButton';
import { api } from '../../../lib/api';
import AuditScoreChip from '../../../components/crm/AuditScoreChip';
import EmptyState from '../../../components/crm/EmptyState';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';

export default function AuditsTab({ account, refreshKey = 0, onAuditCompleted }) {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pending-delete state holds the audit id targeted by the inline trash
  // button. ConfirmDialog reads its open prop from this; clearing the id
  // closes the dialog. Avoids browser-native window.confirm so the UX
  // matches the rest of the admin app.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/accounts/${account.id}/audits`);
      setAudits(res.audits || []);
    } finally { setLoading(false); }
  }, [account.id]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const requestDelete = (auditId, e) => {
    e?.stopPropagation();
    setPendingDeleteId(auditId);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/api/admin/audits/${pendingDeleteId}`);
      await load();
      setPendingDeleteId(null);
    } catch (err) {
      alert(err?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="animate-pulse h-16 rounded-lg bg-white/[0.03]" />;

  const latest = audits[0];
  const recent = audits.slice(0, 5);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="text-[12px] text-gray-500">{audits.length} audit{audits.length === 1 ? '' : 's'}</div>
          {latest && (
            <div className="inline-flex items-center gap-2">
              <span className="text-[11px] text-gray-500">current</span>
              <AuditScoreChip score={latest.score} showLabel />
            </div>
          )}
        </div>
        <RunAuditButton
          account={{ id: account.id, siteUrl: account.siteUrl }}
          variant="solid"
          onCompleted={(result) => { load(); onAuditCompleted?.(result); }}
        />
      </div>

      {audits.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No audits yet" description="Click Run audit now to trigger the first one." />
      ) : (
        <div className="space-y-2">
          {recent.map((a) => (
            <div key={a.id}
              className="w-full p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] flex items-center justify-between gap-3">
              <button onClick={() => navigate(`/admin/audits/${a.id}`)}
                className="flex-1 text-left flex items-center justify-between gap-3">
                <div>
                  <div className="text-[12px] text-gray-400">{a.producedAt}</div>
                  <div className="text-[11px] text-gray-600 mt-0.5">
                    crit {a.violationsCritical ?? 0} · ser {a.violationsSerious ?? 0} · mod {a.violationsModerate ?? 0} · min {a.violationsMinor ?? 0}
                  </div>
                </div>
                <AuditScoreChip score={a.score} showLabel />
              </button>
              <button
                onClick={(e) => requestDelete(a.id, e)}
                aria-label="Delete audit"
                title="Delete this audit"
                className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {audits.length > 5 && (
            <button onClick={() => navigate(`/admin/audits?account_id=${account.id}`)} className="text-[11px] text-[#B0FC38] hover:text-white">
              View all {audits.length} audits →
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDeleteId}
        onClose={() => !deleting && setPendingDeleteId(null)}
        onConfirm={confirmDelete}
        title="Delete this audit?"
        description="Removes the audit row from this account's history. The PDF in storage stays in place. This cannot be undone."
        confirmLabel={deleting ? 'Deleting…' : 'Delete audit'}
        destructive
      />
    </div>
  );
}
